import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Video handling. Anything we turned into an embed URL on the server plays in an
 * iframe; a direct file plays in a <video> tag; anything else falls back to a
 * plain link so a landlord's Facebook video is still reachable.
 */
export const VideoPlayer = ({ item, autoPlay }) => {
  const isEmbed = /youtube\.com\/embed|player\.vimeo\.com/.test(item.url);
  const isFile = /\.(mp4|webm|mov|m4v)(\?|$)/i.test(item.url) || item.source === 'local';

  if (isEmbed) {
    return (
      <iframe
        className="video-frame"
        src={autoPlay ? `${item.url}?autoplay=1` : item.url}
        title={item.caption || 'Property video'}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  if (isFile) {
    return <video className="video-frame" src={item.url} controls autoPlay={autoPlay} preload="metadata" />;
  }

  return (
    <div className="video-fallback">
      <p>This video is hosted somewhere we cannot embed.</p>
      <a className="btn btn-outline" href={item.url} target="_blank" rel="noreferrer noopener">
        Open the video in a new tab
      </a>
    </div>
  );
};

const Arrow = ({ direction }) => (
  <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
    {direction === 'left' ? <path d="M15 5l-7 7 7 7" /> : <path d="M9 5l7 7-7 7" />}
  </svg>
);

/**
 * Image and video carousel for the property detail page, with a thumbnail strip,
 * keyboard arrows and a full-screen view.
 */
const Gallery = ({ media = [], title }) => {
  const items = useMemo(() => media.filter((item) => item.url), [media]);
  const [index, setIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  const go = useCallback(
    (step) => {
      setIndex((current) => {
        if (items.length === 0) return 0;
        return (current + step + items.length) % items.length;
      });
    },
    [items.length]
  );

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'ArrowLeft') go(-1);
      if (event.key === 'ArrowRight') go(1);
      if (event.key === 'Escape') setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  if (items.length === 0) {
    return (
      <div className="gallery gallery-empty">
        <p>The landlord has not added photos yet.</p>
      </div>
    );
  }

  const current = items[Math.min(index, items.length - 1)];

  const stage = (
    <div className="gallery-stage">
      {current.type === 'video' ? (
        <VideoPlayer item={current} />
      ) : (
        <img
          src={current.url}
          alt={current.caption || `${title} - picture ${index + 1}`}
          onClick={() => setFullscreen(true)}
        />
      )}

      {items.length > 1 ? (
        <>
          <button type="button" className="gallery-nav prev" onClick={() => go(-1)} aria-label="Previous">
            <Arrow direction="left" />
          </button>
          <button type="button" className="gallery-nav next" onClick={() => go(1)} aria-label="Next">
            <Arrow direction="right" />
          </button>
        </>
      ) : null}

      <span className="gallery-counter">
        {index + 1} of {items.length}
      </span>
    </div>
  );

  return (
    <>
      <div className="gallery">
        {stage}

        {items.length > 1 ? (
          <div className="gallery-thumbs" role="tablist" aria-label="Pictures and videos">
            {items.map((item, position) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={position === index}
                className={`gallery-thumb ${position === index ? 'is-current' : ''}`}
                onClick={() => setIndex(position)}
              >
                {item.type === 'video' ? (
                  <>
                    {item.thumbnailUrl ? <img src={item.thumbnailUrl} alt="" /> : <span className="thumb-blank" />}
                    <span className="thumb-play" aria-hidden="true">
                      &#9658;
                    </span>
                  </>
                ) : (
                  <img src={item.url} alt="" loading="lazy" />
                )}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {fullscreen ? (
        <div className="lightbox" onClick={() => setFullscreen(false)} role="presentation">
          <button type="button" className="lightbox-close" aria-label="Close">
            &times;
          </button>
          <img src={current.url} alt={current.caption || title} onClick={(event) => event.stopPropagation()} />
        </div>
      ) : null}
    </>
  );
};

export default Gallery;
